<template>
    <widget-wrapper
    class="balances-and-movements"
    :applyShadowToHeader="valueScrolled"
    :variant="variant"
    :title="t('home.widgets.balancesAndMovements.title')"
    :applyErrorState="applyErrorState"
    @redirectToFeature="navigateByName(routeList.ACCOUNTS_BALANCES_AND_MOVEMENTS_NAME, { accountId: selectedAccount })">
        <!-- Loader Generic -->
        <loaders-generic v-if="showAccountsLoader || showOverlayLoader" :fullSize="!showOverlayLoader" :isOverlay="showOverlayLoader"/>
        <!-- Movements List -->
        <div
        v-if="!applyErrorState"
        class="scroll-wrapper"
        ref="scrollElementRef"
        :class="{ 'no-movements-note': !movementsTableData.length, 'mobile': getMobileAppState, 'remove-mobile-padding': getMobileAppState &&  showLoader}"
        @scroll="valueScrolled = $event.target.scrollTop">
            <!-- Account Selector -->
            <account-selector
            v-if="!showLoader && getAccountsList.length"
            onlyAvailableBalance
            classes="border-style"
            :hideAmounts="hideAmounts"
            :selectedOption="selectedAccount"
            :options="getAccountsList"
            @clicked="[selectedAccount = $event, fetchAccountsMovements()]"/>
            <!-- Loader Generic -->
            <loaders-generic v-if="showLoader" fullSize/>
            <!-- Table -->
            <table-group-by-date
            v-if="!!movementsTableData.length && !showLoader"
            hideHeader
            :headerData="movementsTableHeaderData"
            :tableStyles="movementsTableStyles"
            :tableData="movementsTableData"
            :hasMore="getMobileAppState && getAccountMovements.length > 2 && !displayAllMovements"
            @buttonClicked="sendEmailHandler"
            @downloadClicked="downloadClickedHandler"
            @rowClicked="movementsClickHandler($event)"
			@loadMore="displayAllMovements = true"/>
            <!-- Empty-state -->
            <empty-state-generic
            v-if="!movementsTableData.length && !showLoader"
            emptyStateSize="small"
            illustration="emptyState/no-movements"
            :title="t('home.widgets.balancesAndMovements.emptyStateTitle')"
            :description="t('home.widgets.balancesAndMovements.emptyStateDescription')"/>
        </div>
        <!-- Movements Note -->
        <div
        v-if="movementsTableData.length && !showLoader && !getMobileAppState"
        class="movements-note"
        :class="{ 'with-shadow': (!scrollIsAtEnd || valueScrolled === 0) && movementsTableData.length }">{{t('home.widgets.balancesAndMovements.movementsNote')}}</div>
    </widget-wrapper>
    <!-- Send to email Movement Details -->
	<modal-centered-send-info
	v-if="showSendInfoModal"
	emailAction="sendMovementEmailNotification"
	:sendType="'email'"
	:payload="showSendInfoModalData"
	@hideMe="showSendInfoModal = false"/>
    <!-- Movements Details -->
    <movement-detail-modal
    v-if="showMovementsDetails"
    :movementDetails="movementDetails"
    @hideMe="[movementDetails = null, showMovementsDetails = false]"
    @showOverlayLoader="showOverlayLoader = $event"
    @requestListForCategoryUpdate="fetchAccountsMovements('updateCategory')"/>
    <!-- Component to handle download logic-->
    <download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
</template>

<script setup>
import WidgetWrapper from '@/components/features/home/WidgetWrapper'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import AccountSelector from '@/components/generic/selectorsWithDetails/Accounts'
import TableGroupByDate from '@/components/generic/table/GroupByDate'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import MovementDetailModal from '@/components/features/accounts/balancesAndMovements/MovementDetailModal'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'

/* Vue & Assets */
import { ref, watch, computed } from 'vue'
import { categoryIconMapper } from '@/assets/scripts/mappers/categories'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { requestMovementDetailDownloadUrl } from '@/network/api/accounts.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Props
@showAccountsLoader {Boolean} - prop to tell widget accounts fetch state
@applyAccountsErrorState {Boolean} - prop to tell widget accounts fetch state
@variant {String} - can be [ none (default), anchor]
*/
const props = defineProps({
    showAccountsLoader: Boolean,
    applyAccountsErrorState: Boolean,
    variant: String,
    hideAmounts: Boolean
})

/* mapActions & mapGetters */
const { requestAccountsMovements } = mapActions(moduleMapper.HOME)
const { getAccountsList, getAccountMovements } = mapGetters(moduleMapper.HOME)
const { getMobileAppState } = mapGetters()

/* Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)
const applyErrorState = ref(props.applyAccountsErrorState)
const selectedAccount = ref(null)
const valueScrolled = ref(0)
const scrollElementRef = ref('')

const scrollIsAtEnd = computed(() => {
    return scrollElementRef.value.clientHeight + valueScrolled.value === scrollElementRef.value.scrollHeight
})

/*************************************************
*                                                *
*                  Request Logics                *
*                                                *
*************************************************/
watch(() => props.showAccountsLoader, (newVal, oldVal) => {
    if (!props.showAccountsLoader && getAccountsList.value.length) {
        /* Setting Initial Selected Account */
		selectedAccount.value = getAccountsList.value.find(obj => obj.default).id
        fetchAccountsMovements()
    }
})

watch(() => props.applyAccountsErrorState, (newVal, oldVal) => {
    if (props.applyAccountsErrorState) applyErrorState.value = true
})

const fetchAccountsMovements = async (updateCategory) => {
    updateCategory ? showOverlayLoader.value = true : showLoader.value = true

    const payload = {
        accountId: selectedAccount.value,
        params: { pageNumber: 1, pageSize: 10 }
    }

	try {
        await requestAccountsMovements(payload)
        if (updateCategory) {
            movementsClickHandler({ rowId: `${movementDetails.value.id}${movementDetails.value.orderNumberPosition}${movementDetails.value.releaseDatePosition}` })
            showOverlayLoader.value = false
        } else showLoader.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showLoader.value = false
        showOverlayLoader.value = false
	}
}

/*************************************************
*                                                *
*                     Table Data                 *
*                                                *
*************************************************/
const displayAllMovements = ref(false) /* prop to control what we show in mobile */

const movementsTableHeaderData = [
	{ showInMobile: true, dataAttr: 'category', rowCellType: 'category' },
	{ dataAttr: 'description', showInMobile: { groupWith: 'amount' }, rowCellType: 'title' },
	{ dataAttr: 'amount', rowCellType: 'amount' },
    { dataAttr: 'email', rowCellType: 'button', showInMobile: true },
    { dataAttr: 'download', rowCellType: 'download', showInMobile: true }
]

const movementsTableStyles = [null, null, { 'text-align': 'end' }, null, null]

const movementsTableData = computed(() => {
	const movements = getAccountMovements.value.map(movement => {
		return {
			rowDate: movement.transactionDate,
			rowData: [
				{ value: `${movement.id}${movement.orderNumberPosition}${movement.releaseDatePosition}`, dataAttr: 'id' },
				{
					type: 'category',
					value: {
						backgroundColor: movement.categoryView ? movement.categoryView.color : null,
						icon: movement.categoryView ? categoryIconMapper(movement.categoryView.name) : 'brokenImage'
					},
					dataAttr: 'category'
				},
				{ type: 'title', value: movement.description, dataAttr: 'description' },
				{
                    type: 'amount',
                    value: {
                        content: movement.amount,
                        currency: movement.currency,
                        noBalanceIcon: false,
                        hasArrowIcons: true,
                        hideAmounts: props.hideAmounts
                    },
                    dataAttr: 'amount'
                },
                { type: 'button', dataAttr: 'sendEmail', value: 'letter' },
                { type: 'download', dataAttr: 'download' }
			]
		}
	})

    return displayAllMovements.value || !getMobileAppState.value ? movements : movements.slice(0, 2)
})

/*************************************************
*                                                *
*              Table Clicks Handlers             *
*                                                *
*************************************************/
const sendInfoDownloadData = rowId => {
    const movementDetails = getAccountMovements.value.find(movement => `${movement.id}${movement.orderNumberPosition}${movement.releaseDatePosition}` === rowId)

    return {
		operationId: movementDetails.id,
		movementId: movementDetails.id,
		accountId: selectedAccount.value,
		params: {
			releaseDatePosition: movementDetails.releaseDatePosition,
			orderNumberPosition: movementDetails.orderNumberPosition
		}
	}
}
const showSendInfoModal = ref(false)
const showSendInfoModalData = ref(null)
const sendEmailHandler = $event => {
    showSendInfoModal.value = true
    showSendInfoModalData.value = sendInfoDownloadData($event.rowId)
}

const downloadLink = ref(null)
const downloadClickedHandler = rowId => {
    downloadLink.value = requestMovementDetailDownloadUrl(sendInfoDownloadData(rowId))
}

const movementDetails = ref(null)
const showMovementsDetails = ref(false)
const movementsClickHandler = row => {
	const movement = getAccountMovements.value.find(movement => `${movement.id}${movement.orderNumberPosition}${movement.releaseDatePosition}` === row.rowId)
    const accountType = getAccountsList.value.find(account => account.id === selectedAccount.value)?.type

	movementDetails.value = {
		...movement,
		accountId: selectedAccount.value,
		accountType: accountType
	}
	showMovementsDetails.value = true
}
</script>

<style lang="scss" scoped>
.scroll-wrapper {
    width: 100%;
    height: calc(100% - 16px);
    padding: 16px;

    overflow: auto;
    @include generic-scrollbar(null, var(--primary));

    &.no-movements-note {
        height: 100%;
    }

    .selector-wrapper {
        width: 100%;
        margin-bottom: 16px;

        :deep(.dropdown-wrapper) {
            width: 50%;
            min-width: 236px;

            .dropdown-animation-wrapper {
                left: 8px;
                top: 8px !important; /* I didnt want to use this i swear, but I dont think it's better to add infinite logic on dropdown component just for this */
                bottom: unset !important; /* I didnt want to use this i swear, but I dont think it's better to add infinite logic on dropdown component just for this */
                max-height: 230px;
            }
        }

        :deep(.text-blocks-container) {
            overflow: hidden;
        }
    }

    .date-block-wrapper {
        :deep(.group-wrapper) {
            padding-top: 0;

            .table-bed {
                padding-top: 8px;
            }
        }
    }

    &.mobile {
        height: fit-content;
        padding: 16px 0 0 0;

        .selector-wrapper {
            width: calc(100% - 32px);
            margin: 0 auto 16px auto;

            :deep(.dropdown-wrapper) {
                width: 100%;
            }
        }
    }

    &.remove-mobile-padding {
        padding: 16px;
    }
}

.movements-note {
    width: 100%;
    height: 32px;
    padding: 0 16px;

    @include H6-Regular;
    color: var(--secondary-dark-4);
    line-height: 32px;

    transform: translateZ(0);

    &.with-shadow {
        box-shadow: var(--box-shadow-1);
    }
}
</style>
